import { DateMode, MatrixColumnType, type FormField, type FormFieldChoice, type MatrixColumn, type MatrixRow,
  type MatrixTableRow, type MatrixValue } from './form';
import { getLocalTimezoneDetails, getOnlyTimeSeconds, getTimeFromSeconds, getUTCJSDate, getUTCSeconds } from '@/utils';
import { DateTime } from 'luxon';

/**
 * A matrix field's answer, cell by cell: how a cell is written into the save body, how a saved cell
 * is read back into its column, and when the whole matrix counts as answered (ENG-9018 decisions 3,
 * 5 and 6). `fieldAnswer.ts` asks this file for the matrix's row.
 */

/** Reads one saved cell back into `column`, the row's copy of the configured column. */
export const assignColumnValues = (rowConfig: MatrixTableRow, row: MatrixRow, column: MatrixColumn, matrixValues: MatrixValue[]) => {
  for (const rowData of matrixValues) {
    if (rowData.rowId == `${row.id}`) {
      rowConfig.dynamicRows = rowData.dynamicRows;

      for (const columnData of rowData.values) {
        if (columnData.id == `${column.id}` ) {
          column.conditions = columnData.conditions;
          if (columnData.value != '') {
            switch (column.type) {
              case MatrixColumnType.TEXT:
                column.textValue = columnData.value as string;
                break;

              case MatrixColumnType.NUMBER:
                if (column.options?.range) {
                  column.textValue = columnData.value as string;
                } else {
                  column.numberValue = columnData.value ? parseFloat(columnData.value as string) : null;
                }
                break;

              case MatrixColumnType.SINGLE_CHOICE:
              case MatrixColumnType.MULTIPLE_CHOICE:
                column.booleanValue = columnData.value === 'true';
                break;

              case MatrixColumnType.DATE:
                if (!columnData.value) {
                  break;
                }
                if (column.options?.mode === DateMode.DATE_TIME) {
                  if (column.options.absoluteDate) {
                    // when absolute date is selected, it means the time is recorded in UTC seconds
                    column.dateValue = getUTCJSDate(parseInt(columnData.value as string, 10));
                  } else {
                    column.dateValue = DateTime.fromSeconds(parseInt(columnData.value as string)).toJSDate();
                  }
                  if (columnData.timezoneRecorded) {
                    column.timezoneRecorded = typeof columnData.timezoneRecorded === 'string' ?
                      JSON.parse(columnData.timezoneRecorded) :
                      columnData.timezoneRecorded;
                  } else {
                    column.timezoneRecorded = getLocalTimezoneDetails();
                  }
                } else if (column.options?.mode === DateMode.DATE) {
                  column.dateValue = getUTCJSDate(parseInt(columnData.value as string, 10));
                } else if (column.options?.mode === DateMode.TIME) {
                  column.dateValue = getTimeFromSeconds(parseInt(columnData.value as string, 10));
                }
                if (!column.options) {
                  column.options = {};
                }
                if (columnData.dateShifting) {
                  column.options.dateShifting = columnData.dateShifting;
                }
                break;

              case MatrixColumnType.DROPDOWN:
                if (column.options?.multipleChoice) {
                  const values = (columnData.value as string).split(',');
                  const choices = [];
                  for (const choice of column.options.choices ?? []) {
                    for (const value of values) {
                      if (choice.value == value) {
                        choices.push(choice);
                        break;
                      }
                    }
                  }
                  column.choiceValue = choices;
                } else {
                  const choice = column.options?.choices?.find(choiceItem => choiceItem.value == columnData.value);
                  if (choice) {
                    column.choiceValue = choice;
                  }
                }
                break;

              case MatrixColumnType.FILE:
                if (columnData.value) {
                  column.fileIds = JSON.parse(columnData.value as string);
                }
                break;

              default:
                column.textValue = columnData.value as string;
                break;
            }
          }

          break;
        }
      }
      break;
    }
  }
};

type ColumnValue = { value: string; timezoneRecorded: { timezone: string }}

function isColumnValue(value: unknown): value is ColumnValue {
  return (
    value !== null &&
    typeof value === 'object' &&
    'value' in value
  );
}

/** One cell as the save body writes it. A number cell holding 0 writes `"0"` (ADR 0009). */
export const getColumnValue = (column: MatrixColumn) => {
  let value : string | number | ColumnValue = column.textValue ? column.textValue : '';

  switch (column.type) {
    case MatrixColumnType.NUMBER:
      if (!column.options?.range) {
        value = column.numberValue != null && !Number.isNaN(column.numberValue) ? `${column.numberValue}` : '';
      }
      break;

    case MatrixColumnType.DATE:
      if (column.dateValue && typeof column.dateValue === 'object') {
        if (column.options?.mode === DateMode.DATE) {
          value = getUTCSeconds(column.dateValue);
        } else if (column.options?.mode === DateMode.DATE_TIME) {
          if (column.options.absoluteDate) {
            value = getUTCSeconds(column.dateValue);
            return {
              value,
              timezoneRecorded: column.timezoneRecorded
            };
          } else {
            return {
              value: column.dateValue.getTime() / 1000,
              timezoneRecorded: column.timezoneRecorded
            };
          }
        } else {
          value = getOnlyTimeSeconds(column.dateValue);
        }
      }
      break;

    case MatrixColumnType.SINGLE_CHOICE:
    case MatrixColumnType.MULTIPLE_CHOICE:
      value = column.booleanValue ? `${column.booleanValue}` : '';
      break;

    // An upload or a removal writes `textValue`, but a cell loaded from a saved entry has only its `fileIds`.
    case MatrixColumnType.FILE:
      if (!column.textValue && column.fileIds) {
        value = JSON.stringify(column.fileIds);
      }
      break;

    case MatrixColumnType.DROPDOWN:
      if (column.options?.multipleChoice) {
        const selectedChoices: string[] = [];
        (column.choiceValue as FormFieldChoice[])?.forEach(choice => {
          selectedChoices.push(`${choice.value}`);
        });

        value = selectedChoices.join(',');
      } else {
        value = (column.choiceValue as FormFieldChoice)?.value;
      }
      break;

    default:
      break;
  }
  return value;
};

/** The matrix as the save body writes it, or `undefined` when no cell holds anything. */
export const getMatrixValues = (matrixData: MatrixTableRow[]) => {
  const matrixValues = [];

  for (const tableRow of matrixData) {
    const columnValues = [];

    for (const column of tableRow.columnValues ?? []) {
      const value = getColumnValue(column);

      if (isColumnValue(value)) {
        columnValues.push({
          id: `${column.id}`,
          ...(value as ColumnValue)
        });
      } else {
        // Only a cell with nothing in it goes blank: a time at midnight and a choice valued 0 are answers.
        columnValues.push({
          id: `${column.id}`,
          value: value === undefined || value === null || value === '' || Number.isNaN(value) ? '' : `${value}`
        });
      }
    }

    let dynamicRows: string[] | undefined;
    if (tableRow.dynamicRows && tableRow.dynamicRows.length > 0) {
      dynamicRows = tableRow.dynamicRows;
    }

    matrixValues.push({
      rowId: tableRow.rowId,
      values: columnValues,
      dynamicRows
    });
  }

  let hasValue = false;

  for (const matrixValue of matrixValues) {
    for (const value of matrixValue.values) {
      if (value.value !== '' && value.value != null) {
        hasValue = true;
      }
    }
  }

  return hasValue ? matrixValues : undefined;
};

/** A cell as its column holds it: text, a number, a tick, a date, the picked choice values, or file ids. */
export type MatrixCellValue = string | number | boolean | Date | (string | number)[] | null | undefined;

const readText = (cell: MatrixColumn): MatrixCellValue => cell.textValue;

/*
 * One reader per column type, keyed by the enum's string values for the reason `fieldAnswer.ts`
 * gives, so a new column type fails `tsc` until it has one.
 */
const CELL_READERS: Record<MatrixColumnType, (cell: MatrixColumn) => MatrixCellValue> = {
  Text: readText,
  Number: cell => cell.options?.range ? cell.textValue : cell.numberValue,
  RadioButton: cell => cell.booleanValue,
  Checkbox: cell => cell.booleanValue,
  Date: cell => cell.dateValue,
  Dropdown: cell => Array.isArray(cell.choiceValue) ? cell.choiceValue.map(choice => choice.value) : cell.choiceValue?.value,
  File: cell => cell.fileIds
};

// A column type the client doesn't know reads as text, which is how the save body writes it.
const readCell = (cell: MatrixColumn) => (CELL_READERS[cell.type] ?? readText)(cell);

/**
 * Reads the cell in `row` under column `columnId`, decoded rather than as the wire string, so that
 * validation across cells (ENG-9023, decision 5) can reuse it.
 */
export const readMatrixCell = (row: MatrixTableRow, columnId: number | string): MatrixCellValue => {
  const cell = row.columnValues?.find(column => `${column.id}` === `${columnId}`);
  return cell ? readCell(cell) : undefined;
};

/** Blank: nothing, `''`, an unticked choice, a NaN a formula left, or an empty list (a file cell's `"[]"`). */
export const isMatrixCellBlank = (value: MatrixCellValue) =>
  value == null || value === '' || value === false || Number.isNaN(value) || (Array.isArray(value) && value.length === 0);

/**
 * A matrix whose columns are all single or multiple choice is answered when every row has a value;
 * any other matrix when any cell does. Matrix columns have no required flag, so there is no
 * per-column rule. Presence only: a row summing to the wrong total is still answered.
 */
export const isMatrixAnswered = (field: FormField) => {
  const rows = field.matrixTableRowValues;
  if (!rows?.length) {
    return false;
  }

  const choiceOnly = (field.options.columns ?? []).every(column =>
    column.type === MatrixColumnType.SINGLE_CHOICE || column.type === MatrixColumnType.MULTIPLE_CHOICE);
  const rowAnswered = (row: MatrixTableRow) =>
    (row.columnValues ?? []).some(cell => !isMatrixCellBlank(readCell(cell)));

  return choiceOnly ? rows.every(rowAnswered) : rows.some(rowAnswered);
};
